import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  GetAutomationOutboundTargetsDocument,
  type AutomationOutboundTargetRow,
} from "#app/lib/data";
import { connectionDisplayName } from "#app/hooks/automations/use-automation-inbound-sources";
import {
  asConfigSchema,
  type AutomationConfigField,
} from "#app/hooks/automations/use-automation-catalog";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";

// The generic connection-targeted step type, mirroring the server catalog key in
// automations/catalog/step-types.ts. Defined HERE rather than in
// action-node-fields.tsx because both that component and the add-step picker
// import it alongside these helpers — putting it in either component would make
// the other import a component just for a constant, or cycle back through this
// module.
export const INTEGRATION_OUTBOUND_STEP_KEY = "integration.outbound";

// One outbound-capable connection whose actions an Action node can perform, with
// its provider's declared actions. The row type comes from the colocated
// fragment through `lib/data`, so the shape follows the schema and cannot drift.
export type OutboundTarget = AutomationOutboundTargetRow;

// `undefined` is spelled out because callers pass a graph node's `stepType`
// straight through, and that is `string | undefined` — `exactOptionalPropertyTypes`
// rejects an explicit undefined against a bare optional member.
type StepLike = { type?: string | null | undefined; config?: unknown } | null;

// The connection id a connection-targeted step points at, or null for a plain
// SalesTrak step. Pure — exported so surfaces that hold a step (the canvas node,
// the go-live gate) can classify it without opening a query watcher.
export function outboundConnectionIdOf(step: StepLike): string | null {
  if (step?.type !== INTEGRATION_OUTBOUND_STEP_KEY) return null;
  const cfg = step.config as { connectionId?: unknown } | null | undefined;
  return cfg && typeof cfg === "object" && typeof cfg.connectionId === "string"
    ? cfg.connectionId
    : null;
}

/** The action key a connection-targeted step performs, or null. */
export function outboundActionOf(step: StepLike): string | null {
  if (step?.type !== INTEGRATION_OUTBOUND_STEP_KEY) return null;
  const cfg = step.config as { action?: unknown } | null | undefined;
  return cfg && typeof cfg === "object" && typeof cfg.action === "string"
    ? cfg.action
    : null;
}

// The workspace's outbound-capable connections, for the provider sections of an
// Action node's Action dropdown. `byId` round-trips a saved connection-targeted
// step (its stored connectionId → name + the provider's actions).
//
// cache-and-network for the same reason as the inbound sources: a connection can
// be added, renamed, or disabled while a builder tab is open.
export function useAutomationOutboundTargets() {
  const { canEdit } = useAutomationPermissions();
  const { data, loading, error } = useQuery(GetAutomationOutboundTargetsDocument, {
    fetchPolicy: "cache-and-network",
    skip: !canEdit,
  });

  const targets = useMemo<OutboundTarget[]>(
    () => data?.automationOutboundTargets ?? [],
    [data],
  );
  const byId = useMemo(
    () => new Map(targets.map((t) => [t.connectionId, t])),
    [targets],
  );

  // A viewer who may not read the list is reported as still LOADING rather than
  // as holding an empty one: `useOutboundConnectionDisabled` and the Action
  // dropdown's unavailable-connection row both key off this, and "not permitted
  // to know" must not resolve to "gone".
  //
  // `initialLoading` is the separate signal for "nothing to show YET", and it is
  // what validation gates read — never raw `loading`. The policy is
  // cache-and-network, so `loading` flips true on every remount and background
  // refetch even with data already cached; gating a badge on it drops the badge
  // mid-edit. It carries `!canEdit` for the same reason `isLoading` does: an
  // unreadable list must not be validated against as though it were empty.
  return {
    targets,
    byId,
    isLoading: loading || !canEdit,
    initialLoading: (loading && !data) || !canEdit,
    error,
  };
}

// Whether a connection-targeted step's TARGET connection is disabled. Such a
// step can't succeed — the dispatcher refuses an inactive connection — so the
// node renders deactivated while still showing what it was configured with.
//
// Note the asymmetry with the inbound side: a disabled TRIGGER connection stops
// the whole automation (no event is ever dispatched), whereas a disabled ACTION
// target only kills that one step. The automation still runs, so nothing here
// blocks go-live or the enable toggle.
//
// True only when the target is found AND unusable — a missing target (deleted,
// or a since-gated provider) is not reported as broken, since "gone" and
// "still loading" are indistinguishable and a false flash is worse than none.

/**
 * WHY a connection-targeted step cannot run, or null when it can.
 *
 * Two causes, deliberately distinguished. `disabled` is the workspace's own
 * switch and the fix is to turn it back on. `attention` is the PROVIDER
 * reporting the connection broken — its app uninstalled, its tokens revoked —
 * and telling an author to re-enable it would send them to a toggle that is
 * already on. `message` carries the provider's own sentence for that case.
 */
export type OutboundConnectionProblem = {
  kind: "disabled" | "attention" | "scope";
  /**
   * The provider's sentence, for `attention` and `scope`. Null for a plain
   * disable, where the state IS the explanation.
   */
  message: string | null;
};

export function useOutboundConnectionProblem(): (step: StepLike) => OutboundConnectionProblem | null {
  const { byId } = useAutomationOutboundTargets();
  return useMemo(
    () =>
      (step: StepLike): OutboundConnectionProblem | null => {
        const id = outboundConnectionIdOf(step);
        if (!id) return null;
        const target = byId.get(id);
        if (!target) return null;
        // Disabled outranks attention: it is the state the author can act on
        // from here, and naming a revoked token to someone who has switched the
        // connection off is answering a question they did not ask.
        if (target.active === false) return { kind: "disabled", message: null };
        if (target.needsAttention) {
          return { kind: "attention", message: target.attentionMessage ?? null };
        }
        // Last, and per ACTION: the connection is fine, this one action needs a
        // permission it was never granted. Ranked below the other two because
        // they disable every action, so naming a single action's scope gap
        // while the whole connection is off would answer the wrong question.
        const actionKey = outboundActionOf(step);
        const chosen = actionKey ? target.actions.find((a) => a.key === actionKey) : undefined;
        if (chosen?.unavailableReason) {
          return { kind: "scope", message: chosen.unavailableReason };
        }
        return null;
      },
    [byId],
  );
}

/** Whether the step's target cannot run, for callers that need only the boolean. */
export function useOutboundConnectionDisabled(): (step: StepLike) => boolean {
  const problem = useOutboundConnectionProblem();
  return useMemo(() => (step: StepLike): boolean => problem(step) !== null, [problem]);
}

// A `resolve(entityType, id)` naming a connection-targeted step's parts:
// `connection` (connectionId → its provider-qualified display name) and
// `outboundAction` (action key → its label, deduped across providers). Returns
// null for anything else so a caller chains it after its own cases. Mirrors
// useInboundSourceResolver — including `connectionDisplayName`, so an outbound
// step's caption words its connection exactly like a trigger's.
/**
 * The SELECTED ACTION's config schema for a connection-targeted step, or null
 * for any other step.
 *
 * Needed because the step type's own schema is only `connectionId` + `action`
 * (`dynamicConfig`), so validating an Action node against it checks nothing the
 * provider actually asked for — an empty required field passes on the client
 * and is rejected by the server at publish.
 *
 * Mirrors `AutomationsService.outboundActionSchema` deliberately, including its
 * two quirks: it resolves by ACTION KEY across every target rather than by
 * looking up the step's own connection, and it skips a key claimed by two
 * providers rather than guess which schema applies. The client gate must not be
 * stricter than the server's, or the builder blocks a graph go-live accepts.
 *
 * Returns null while targets load, so no badge flashes on a graph that is fine.
 *
 * `which` selects the schema being resolved — the action's INPUT fields or its
 * extra OUTPUTS. Everything else about resolving them is identical, and the
 * ambiguity rule below is the part worth having exactly once.
 */
function useOutboundActionSchemaFor(
  which: "configSchema" | "outputSchema",
): (step: StepLike) => AutomationConfigField[] | null {
  const { byId, initialLoading } = useAutomationOutboundTargets();
  return useMemo(() => {
    // action key → { the provider that claimed it, its schema }. Null schema
    // once a SECOND PROVIDER claims the same key.
    //
    // Ambiguity is decided by PROVIDER, never by comparing the schemas. `byId`
    // holds one entry per CONNECTION, so two connections of one provider carry
    // the same action twice — and their `configSchema` arrays are two distinct
    // objects parsed from the same response, so any reference comparison calls
    // them different and poisons the key to null. That silently disabled this
    // whole check for any workspace with two connections to one provider.
    // Comparing by
    // value would work but is unnecessary: a provider's actions come from one
    // registry entry, so all its connections have identical schemas by
    // construction, and only a cross-provider key collision is genuinely
    // ambiguous. This is also exactly what the server does — it iterates the
    // registry (one entry per provider) and skips a key matched more than once.
    const schemas = new Map<string, { provider: string; schema: AutomationConfigField[] | null }>();
    for (const target of byId.values()) {
      for (const action of target.actions) {
        const claimed = schemas.get(action.key);
        if (claimed) {
          if (claimed.provider !== target.providerSlug) claimed.schema = null;
          continue;
        }
        schemas.set(action.key, {
          provider: target.providerSlug,
          schema: asConfigSchema(action[which]),
        });
      }
    }
    return (step: StepLike): AutomationConfigField[] | null => {
      const key = outboundActionOf(step);
      // `initialLoading`, not raw loading: suppressing on every background
      // refetch would drop the node's badge and un-taint its edge mid-edit, and
      // let Go Live be clicked for a graph the server then rejects.
      if (!key || initialLoading) return null;
      return schemas.get(key)?.schema ?? null;
    };
  }, [byId, initialLoading, which]);
}

export function useOutboundActionSchema(): (step: StepLike) => AutomationConfigField[] | null {
  return useOutboundActionSchemaFor("configSchema");
}

/**
 * The chosen action's EXTRA outputs — what it returns beyond the generic set the
 * `integration.outbound` step type declares.
 *
 * Shares `useOutboundActionSchemaFor` with the config side rather than repeating
 * the resolution, because the tricky part is identical and worth having in one
 * place: an action key claimed by two DIFFERENT providers is ambiguous and
 * resolves to null, while the same key across two connections of ONE provider is
 * not. Two copies of that rule would eventually disagree, and the failure is
 * silent — a variable that quietly stops being offered.
 */
export function useOutboundActionOutputs(): (step: StepLike) => AutomationConfigField[] | null {
  return useOutboundActionSchemaFor("outputSchema");
}

export function useOutboundTargetResolver(): (
  entityType: string,
  id: string,
) => string | null {
  const { byId } = useAutomationOutboundTargets();
  return useMemo(() => {
    const actionLabels = new Map<string, string>();
    for (const t of byId.values()) {
      for (const a of t.actions) if (!actionLabels.has(a.key)) actionLabels.set(a.key, a.label);
    }
    return (entityType: string, id: string): string | null => {
      if (entityType === "connection") {
        const target = byId.get(id);
        return target ? connectionDisplayName(target) : null;
      }
      if (entityType === "outboundAction") return actionLabels.get(id) ?? null;
      return null;
    };
  }, [byId]);
}
